import type { Opportunity } from '@/api/types'

export type CustomerOpportunityGroup = {
  customerId: string
  customerName: string
  yearTotal: number
  items: Opportunity[]
}

/**
 * Group chrome (headers/subtotals) is only safe when the list payload is already
 * ordered by customer. With keepPreviousData, sortBy can flip to customer_year_total
 * while items are still opportunity-sorted — grouping those produces orphan subtotals.
 */
export function shouldGroupByCustomer(args: {
  sortBy: string
  pageUnit: 'item' | 'customer' | undefined
}): boolean {
  return args.sortBy === 'customer_year_total' && args.pageUnit === 'customer'
}

/** Consecutive same-customer runs; secondary-sorts lines by year total then name. */
export function groupOpportunitiesByCustomer(
  items: Opportunity[],
  sortDir: 'asc' | 'desc',
): CustomerOpportunityGroup[] {
  const groups: CustomerOpportunityGroup[] = []
  for (const item of items) {
    const last = groups[groups.length - 1]
    if (last && last.customerId === item.customer_id) {
      last.items.push(item)
      continue
    }
    groups.push({
      customerId: item.customer_id,
      customerName: item.customer_name?.trim() || '—',
      yearTotal: Number(item.customer_year_total ?? 0),
      items: [item],
    })
  }
  const dir = sortDir === 'asc' ? 1 : -1
  for (const group of groups) {
    group.items = [...group.items].sort((a, b) => {
      const aTotal = Number(a.year_total ?? 0)
      const bTotal = Number(b.year_total ?? 0)
      if (aTotal !== bTotal) return (aTotal - bTotal) * dir
      return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
    })
  }
  return groups
}
